import React from "react";
import { Box, Button, Chip, CircularProgress, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ReplayIcon from "@mui/icons-material/Replay";
import HistoryIcon from "@mui/icons-material/History";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import BoltIcon from "@mui/icons-material/Bolt";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import {
  APPROVAL_STATUS,
  PUSH_STATUS,
  RENDER_STATUS,
  SentContentTooltip,
  StatusChip,
  TimeAgo,
  VIDEO_SOURCE_META,
  getRowState,
} from "./constants";

/**
 * One push, thumbnail-first. This is the default view because approving a
 * video is a visual judgement — the frame matters more than the metadata,
 * which the table view is there for instead.
 */
export default function ApprovalCard({ row, isAdmin, busyId, actions }) {
  const s = getRowState(row, isAdmin);
  const busy = busyId === row.id;
  const accent = APPROVAL_STATUS[row.approval_status]?.color || "#94a3b8";
  const source = VIDEO_SOURCE_META[row.video_source] || VIDEO_SOURCE_META.ORIGINAL;
  const rendering = s.isEdited && row.render_status === "rendering";

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: s.isStale ? "#fcd34d" : "#e6ebf1",
        transition: "box-shadow .18s ease, transform .18s ease, border-color .18s ease",
        "&:hover": {
          boxShadow: "0 10px 24px -12px rgba(15,23,42,.28)",
          transform: "translateY(-2px)",
          borderColor: "#cbd5e1",
        },
        // Status rail — lets a wall of cards be scanned by colour alone.
        "&:before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          bgcolor: accent,
          zIndex: 2,
        },
      }}
    >
      <Box
        onClick={() => s.canPreview && actions.onPreview(row)}
        sx={{
          position: "relative",
          pt: "56.25%",
          bgcolor: "#0b1120",
          cursor: s.canPreview ? "pointer" : "default",
          "&:hover .ap-play": { opacity: 1 },
        }}
      >
        {row.thumbnail_url ? (
          <Box
            component="img"
            src={row.thumbnail_url}
            alt=""
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: rendering ? 0.35 : 1,
            }}
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#334155",
            }}
          >
            <ImageNotSupportedIcon />
          </Box>
        )}

        {rendering ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
            }}
          >
            <CircularProgress size={24} sx={{ color: "#fbbf24" }} />
            <Typography variant="caption" sx={{ color: "#fbbf24", fontWeight: 600 }}>
              Rendering…
            </Typography>
          </Box>
        ) : (
          s.canPreview && (
            <Box
              className="ap-play"
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0,
                transition: "opacity .18s ease",
                background: "linear-gradient(180deg, rgba(2,6,23,.15), rgba(2,6,23,.55))",
              }}
            >
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,.92)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PlayArrowIcon sx={{ color: "#0f172a" }} />
              </Box>
            </Box>
          )
        )}

        <Box sx={{ position: "absolute", top: 8, left: 10, display: "flex", gap: 0.5 }}>
          <Chip
            size="small"
            label={source.label}
            sx={{
              height: 20,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.3,
              textTransform: "uppercase",
              color: source.color,
              bgcolor: source.bg,
              "& .MuiChip-label": { px: 0.8 },
            }}
          />
        </Box>
        {row.is_force_push && (
          <Tooltip title="Force push — bypasses Admin Portal's queue and publishes immediately">
            <Chip
              size="small"
              icon={<BoltIcon sx={{ fontSize: 13, color: "#92400e !important" }} />}
              label="Force"
              sx={{
                position: "absolute",
                top: 8,
                right: 10,
                height: 20,
                fontSize: 10,
                fontWeight: 700,
                color: "#92400e",
                bgcolor: "#fef3c7",
                "& .MuiChip-label": { px: 0.6 },
              }}
            />
          </Tooltip>
        )}
      </Box>

      <Box sx={{ p: 1.5, pl: 1.75, flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        <Box>
          <Tooltip title={<SentContentTooltip s={s} />} placement="top-start">
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {s.title}
            </Typography>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{ color: "#94a3b8", fontFamily: "monospace", fontSize: 11, letterSpacing: 0.2 }}
          >
            {s.key}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
          <Box display="flex" alignItems="center" gap={0.7} minWidth={0}>
            <RssFeedIcon sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }} />
            <Tooltip title={`${row.admin_portal_group_label || ""} (${row.admin_portal_group_type || "—"})`}>
              <Typography variant="caption" color="text.secondary" noWrap>
                {s.target}
              </Typography>
            </Tooltip>
          </Box>
          <Box display="flex" alignItems="center" gap={0.7}>
            <ScheduleIcon sx={{ fontSize: 13, color: s.isStale ? "#b45309" : "#94a3b8", flexShrink: 0 }} />
            <TimeAgo iso={row.created_date} prefix="Created " stale={s.isStale} />
          </Box>
          {row.approved_at && (
            <Box display="flex" alignItems="center" gap={0.7}>
              {row.approval_status === "REJECTED" ? (
                <BlockIcon sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }} />
              ) : (
                <CheckCircleOutlineIcon sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }} />
              )}
              <TimeAgo
                iso={row.approved_at}
                prefix={`${row.approval_status === "REJECTED" ? "Rejected" : "Approved"} `}
              />
              {row.approved_by && (
                <Typography variant="caption" color="text.disabled" noWrap>
                  by {row.approved_by}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Box display="flex" gap={0.5} flexWrap="wrap">
          <StatusChip map={APPROVAL_STATUS} value={row.approval_status} />
          <StatusChip map={PUSH_STATUS} value={row.status} />
          {s.isEdited && row.render_status && <StatusChip map={RENDER_STATUS} value={row.render_status} />}
        </Box>

        {(row.error_message || row.rejection_reason) && (
          <Box
            sx={{
              px: 1,
              py: 0.6,
              borderRadius: 1.5,
              bgcolor: "#fef2f2",
              border: "1px solid #fecaca",
            }}
          >
            <Tooltip title={row.error_message || row.rejection_reason}>
              <Typography
                variant="caption"
                sx={{
                  color: "#b91c1c",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {row.error_message || row.rejection_reason}
              </Typography>
            </Tooltip>
          </Box>
        )}

        <Box flex={1} />

        <Box display="flex" alignItems="center" gap={0.5} pt={0.5} borderTop="1px solid #f1f5f9">
          {isAdmin && s.isPending ? (
            <>
              <Tooltip title={s.approveDisabledReason}>
                <span style={{ flex: 1 }}>
                  <Button
                    fullWidth
                    size="small"
                    variant="contained"
                    disableElevation
                    startIcon={<ThumbUpIcon sx={{ fontSize: 15 }} />}
                    disabled={busy || !s.canApprove}
                    onClick={() => actions.onApprove(row.id)}
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      bgcolor: "#10b981",
                      fontSize: 12.5,
                      "&:hover": { bgcolor: "#059669" },
                    }}
                  >
                    Approve
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Reject this push">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={busy}
                    onClick={() => actions.onReject(row.id)}
                    sx={{ textTransform: "none", borderRadius: 2, minWidth: 0, px: 1.2, fontSize: 12.5 }}
                  >
                    <ThumbDownIcon sx={{ fontSize: 15 }} />
                  </Button>
                </span>
              </Tooltip>
            </>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              disabled={!s.canPreview}
              onClick={() => actions.onPreview(row)}
              startIcon={<PlayArrowIcon sx={{ fontSize: 16 }} />}
              sx={{ flex: 1, textTransform: "none", borderRadius: 2, borderColor: "#e2e8f0", fontSize: 12.5 }}
            >
              Preview
            </Button>
          )}

          {s.canRetry && (
            <Tooltip title={row.error_message ? `Retry: ${row.error_message}` : "Retry on the next scheduler tick"}>
              <span>
                <IconButton
                  size="small"
                  disabled={busy}
                  onClick={() => {
                    // Admin Portal's duplicate-push guard (409) means a plain
                    // retry will just 409 again — only force it through with
                    // allow_duplicate once an admin has explicitly confirmed
                    // they want a second copy in that feed.
                    const isDuplicate = /already (in|queued for)/i.test(row.error_message || "");
                    if (isDuplicate && !window.confirm(
                      `${row.error_message}\n\nRetry anyway and push it again as a duplicate?`
                    )) {
                      return;
                    }
                    actions.onRetry(row.id, isDuplicate);
                  }}
                >
                  <ReplayIcon sx={{ fontSize: 17, color: "#f59e0b" }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="View full logs — every step this push has been through">
            <IconButton size="small" onClick={() => actions.onLogs(row)}>
              <HistoryIcon sx={{ fontSize: 17, color: "#64748b" }} />
            </IconButton>
          </Tooltip>
          {s.canDelete && (
            <Tooltip title="Delete this push and any files it still holds (S3 + local)">
              <span>
                <IconButton size="small" disabled={busy} onClick={() => actions.onDelete(row)}>
                  <DeleteForeverIcon sx={{ fontSize: 17, color: "#dc2626" }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
